import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Menu, MenuList } from '@material-ui/core';
import { useMediaQueries } from '../../MediaQueries';
import { useDevice } from '../../providers/Device';
import { useIntentBehavior } from '../../providers/IntentBehavior';
import { isTestMode } from '../../utils/test-mode';
import { useBiAction } from '../BiTracking';
import { BottomSheetContentLayout } from '../BottomSheet';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { Box, Row } from '../Layout';
import { MenuItem } from '../MenuItem/MenuItem';
import { useSheetStack } from '../OverlayContext/hooks/useSheetStack';
import { Tooltip } from '../Tooltip';
import { useDropdownMenuStyles } from './DropdownMenu.styles';
import { computeMenuOrigins, DropdownMenuItemTestIds } from './DropdownMenu.utils';
const DropdownMenuCloseContext = /*#__PURE__*/ React.createContext(null);
export function DropdownMenu(props) {
    const { isMobileDevice } = useDevice();
    const { smallerThanTablet } = useMediaQueries();
    const useBottomSheet = isMobileDevice || smallerThanTablet;
    const Component = useBottomSheet ? DropdownMenuMobile : DropdownMenuDesktop;
    return /*#__PURE__*/ _jsx(Component, _object_spread({}, props));
}
export function DropdownMenuMobile(props) {
    const { target, onOpenChange, children, mobile } = props;
    const { pushBottomSheetPage, popBottomSheetPage } = useSheetStack();
    const header = (mobile === null || mobile === void 0 ? void 0 : mobile.title) || (mobile === null || mobile === void 0 ? void 0 : mobile.subtitle) ? {
        title: mobile.title,
        subtitle: mobile.subtitle
    } : undefined;
    const clonedTarget = /*#__PURE__*/ React.cloneElement(target, {
        onClick: (event)=>{
            pushBottomSheetPage({
                content: /*#__PURE__*/ _jsx(DropdownMenuCloseContext.Provider, {
                    value: popBottomSheetPage,
                    children: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                        header: header,
                        children: /*#__PURE__*/ _jsx(MenuList, {
                            disablePadding: true,
                            children: children
                        })
                    })
                }),
                onClose: ()=>onOpenChange && onOpenChange(false),
                variant: mobile === null || mobile === void 0 ? void 0 : mobile.variant
            });
            onOpenChange && onOpenChange(true);
            target.props.onClick && target.props.onClick(event);
        }
    });
    return clonedTarget;
}
export function DropdownMenuDesktop(props) {
    const { target, children, hAlign = 'right', vAlign = 'bottom', gap = 8, onOpenChange, onClose, id, promotionalAction, promotionalActionOnClick } = props;
    const overrides = useDropdownMenuStyles({
        minWidth: props.minWidth,
        maxHeight: props.maxHeight,
        maxWidth: props.maxWidth,
        width: props.width
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const menuId = useMemo(()=>id || Math.random().toString(), [
        id
    ]);
    const { anchorOrigin, transformOrigin } = computeMenuOrigins(vAlign, hAlign, gap);
    const clonedTarget = /*#__PURE__*/ React.cloneElement(target, {
        'aria-owns': anchorEl ? menuId : undefined,
        'aria-haspopup': 'true',
        onClick: (event)=>{
            setAnchorEl(event.currentTarget);
            onOpenChange && onOpenChange(true);
            target.props.onClick && target.props.onClick(event);
        }
    });
    const closeMenu = useCallback(()=>setAnchorEl(null), []);
    return /*#__PURE__*/ _jsxs(DropdownMenuCloseContext.Provider, {
        value: closeMenu,
        children: [
            clonedTarget,
            /*#__PURE__*/ _jsxs(Menu, {
                classes: {
                    paper: overrides.menu
                },
                id: menuId,
                anchorEl: anchorEl,
                open: Boolean(anchorEl),
                autoFocus: false,
                onClose: (event, reason)=>{
                    onClose && onClose(anchorEl, reason, event);
                    setAnchorEl(null);
                },
                TransitionProps: {
                    onExited () {
                        onOpenChange && onOpenChange(false);
                    }
                },
                getContentAnchorEl: null,
                transitionDuration: isTestMode ? 0 : 'auto',
                anchorOrigin: anchorOrigin,
                transformOrigin: transformOrigin,
                "data-testid": props.testId,
                children: [
                    children,
                    promotionalAction && /*#__PURE__*/ _jsx(Box, {
                        onClick: promotionalActionOnClick,
                        className: overrides.promotionalAction,
                        children: promotionalAction
                    })
                ]
            })
        ]
    });
}
// MUI's Menu clones the active child with a ref to track the content anchor — keep
// forwardRef to avoid React's "Function components cannot be given refs" warning.
// forwardRef's PropsWithoutRef collapses the BiProps discriminated union, so cast the
// result back to a plain function shape with `ref` attached so consumers see the
// discriminated union at the JSX boundary.
const DropdownMenuItemImpl = /*#__PURE__*/ React.forwardRef(function DropdownMenuItem(props, ref) {
    const { children, iconStart, iconEnd, skin = 'secondary', as, disabled, intent, tooltipProps, closeOnClick, bi, noBi: _noBi, onClick, testId } = props, rest = _object_without_properties(props, [
        "children",
        "iconStart",
        "iconEnd",
        "skin",
        "as",
        "disabled",
        "intent",
        "tooltipProps",
        "closeOnClick",
        "bi",
        "noBi",
        "onClick",
        "testId"
    ]);
    const { getDisabled, getTooltip } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const tooltipFromIntent = intent ? getTooltip(intent) : null;
    const isDisabled = disabledFromIntent || disabled;
    const logBiAction = useBiAction();
    const closeMenu = useContext(DropdownMenuCloseContext);
    const handleClick = useCallback((event, biContext)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'dropdown_menu_item',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        if (closeOnClick) {
            closeMenu === null || closeMenu === void 0 ? void 0 : closeMenu();
        }
        return onClick === null || onClick === void 0 ? void 0 : onClick(event, biContext);
    }, [
        bi,
        closeMenu,
        closeOnClick,
        logBiAction,
        onClick,
        testId
    ]);
    return /*#__PURE__*/ _jsx(ConditionalWrapper, {
        condition: !!(tooltipProps === null || tooltipProps === void 0 ? void 0 : tooltipProps.title) || !!tooltipFromIntent,
        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({
                testId: DropdownMenuItemTestIds.Tooltip(testId !== null && testId !== void 0 ? testId : '')
            }, tooltipProps || {
                placement: 'top',
                title: tooltipFromIntent
            }), {
                children: isDisabled ? /*#__PURE__*/ _jsx("div", {
                    "data-testid": DropdownMenuItemTestIds.Wrapper(testId !== null && testId !== void 0 ? testId : ''),
                    children: children
                }) : children
            })),
        children: /*#__PURE__*/ _jsx(MenuItem, _object_spread_props(_object_spread({}, rest), {
            onClick: handleClick,
            disabled: isDisabled,
            component: as,
            skin: skin,
            ref: ref,
            testId: testId,
            children: /*#__PURE__*/ _jsxs(Row, {
                gap: 12,
                alignItems: "center",
                children: [
                    iconStart,
                    /*#__PURE__*/ _jsx("span", {
                        children: children
                    }),
                    iconEnd
                ]
            })
        }))
    });
});
DropdownMenuItemImpl.defaultProps = {
    closeOnClick: true
};
export const DropdownMenuItem = DropdownMenuItemImpl;
DropdownMenu.Item = DropdownMenuItem;
